import { Logout, Menu } from "@mui/icons-material";
import {
  BoxesNavIcon,
  CamerasNavIcon,
  DashboardNavIcon,
  EventsNavIcon,
  IncidentsNavIcon,
  IntegrationsNavIcon,
  ProfileNavIcon,
  SitesNavIcon,
  UsersNavIcon
} from "components/SidebarNavIcons";
import {
  AppBar,
  Box,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ThemeProvider,
  Toolbar,
  useMediaQuery,
  useTheme
} from "@mui/material";
import { COLORS, contentTheme } from "assets";
import { BreakGlassModal, StyledListItem } from "components";
import { drawerWidth, ENDPOINTS } from "config/constants";
import { useContextController } from "context";
import { incidentDocsOn, navigationWithoutIncidents, useIncidentDocs } from "incidents/feature";
import { logout } from "context/actions";
import { useFetch } from "hooks";
import useTranslation from "hooks/useTranslation";
import { Fragment, PropsWithChildren, ReactNode, useCallback, useMemo, useState } from "react";
import { CanAccess } from "types";

const NAVIGATION: {
  name: string;
  path: string;
  icon: ReactNode;
  canAccess: CanAccess;
}[] = [
  {
    name: "Dashboard",
    path: "/dashboard",
    icon: <DashboardNavIcon size={20} />,
    canAccess: () => true
  },
  {
    name: "Users",
    icon: <UsersNavIcon size={20} />,
    path: "/users",
    canAccess: (type) => type === "super"
  },
  {
    name: "Sites",
    path: "/sites",
    icon: <SitesNavIcon size={20} />,
    canAccess: () => true
  },
  {
    name: "Integrations",
    path: "/integrations",
    icon: <IntegrationsNavIcon size={20} />,
    canAccess: (type, role) => !(type === "user" && role === "user")
  },
  {
    name: "Surveillance Boxes",
    path: "/boxes",
    icon: <BoxesNavIcon size={20} />,
    canAccess: () => true
  },
  {
    name: "Cameras",
    path: "/cameras",
    icon: <CamerasNavIcon size={20} />,
    canAccess: () => true
  },
  {
    name: "Events",
    path: "/threats",
    icon: <EventsNavIcon size={20} />,
    canAccess: () => true
  },
  {
    name: "Incidents",
    path: "/incidents",
    icon: <IncidentsNavIcon size={20} />,
    canAccess: () => true
  },
  {
    name: "Profile",
    path: "/profile",
    icon: <ProfileNavIcon size={20} />,
    canAccess: () => true
  }
];

export default function UserLayout({ children }: PropsWithChildren) {
  const [{ userData }, dispatch] = useContextController();
  const { callFetch } = useFetch("DELETE");
  const theme = useTheme();
  const isMediumScreen = useMediaQuery(theme.breakpoints.up("md"));
  const [open, setOpen] = useState(false);

  const incidentDocs = incidentDocsOn(useIncidentDocs());
  const navigations = NAVIGATION;
  const navigation = useMemo(
    () =>
      navigationWithoutIncidents(
        navigations.filter((item) => userData && item.canAccess(userData.type, userData.role)),
        incidentDocs
      ),
    [navigations, userData, incidentDocs]
  );
  const openDrawer = useCallback(() => setOpen(true), []);
  const closeDrawer = useCallback(() => setOpen(false), []);
  const onLogout = useCallback(() => {
    localStorage.setItem("logout", "true");
    callFetch(ENDPOINTS.account).catch(console.error);
    logout(dispatch);
  }, [callFetch, dispatch]);
  const t = useTranslation();

  const shellBackgroundSx = {
    backgroundColor: COLORS.gradients.sidebar.bottom,
    backgroundImage: COLORS.appShellBackground,
    backgroundAttachment: "fixed",
    backgroundRepeat: "no-repeat",
    backgroundSize: "cover"
  };

  return (
    <Fragment>
      {!isMediumScreen && (
        <AppBar
          position="static"
          sx={{
            ...shellBackgroundSx,
            backgroundAttachment: "scroll"
          }}
        >
          <Toolbar className="toolbar">
            <IconButton size="large" edge="start" color="inherit" aria-label="menu" onClick={openDrawer} sx={{ mr: 2 }}>
              <Menu />
            </IconButton>
          </Toolbar>
        </AppBar>
      )}
      <Drawer
        anchor="left"
        PaperProps={{
          sx: {
            width: drawerWidth,
            height: "100dvh",
            maxHeight: "100dvh",
            backgroundColor: "transparent",
            backgroundImage: "none",
            border: 0,
            boxShadow: "none",
            overflow: "visible"
          }
        }}
        variant={isMediumScreen ? "permanent" : "temporary"}
        open={open}
        onClose={closeDrawer}
      >
        <Box
          width={drawerWidth}
          role="presentation"
          onClick={closeDrawer}
          onKeyDown={closeDrawer}
          display="flex"
          flexDirection="column"
          height="100%"
          maxHeight="100dvh"
          py={1.5}
          px={1.5}
          boxSizing="border-box"
          position="relative"
          zIndex={1}
          overflow="hidden"
        >
          <Box
            component="img"
            src="/neuravue-logo.png"
            alt="NeuraVue"
            sx={{
              width: "100%",
              maxWidth: 220,
              height: "auto",
              maxHeight: 64,
              objectFit: "contain",
              objectPosition: "center",
              alignSelf: "center",
              mb: 2.5,
              flexShrink: 0
            }}
          />
          <List
            dense
            disablePadding
            sx={{
              flex: 1,
              minHeight: 0,
              width: "100%",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              gap: 0.5,
              justifyContent: "flex-start"
            }}
          >
            {navigation.map(({ name, icon, path }, i) => (
              <ListItem key={i} disablePadding sx={{ display: "block", flexShrink: 1 }}>
                <StyledListItem selected={window.location.pathname.includes(path)} to={path}>
                  <ListItemIcon sx={{ minWidth: 34, color: "inherit", display: "flex", alignItems: "center" }}>
                    {icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={t[name]}
                    primaryTypographyProps={{
                      fontSize: "0.875rem",
                      fontWeight: 500,
                      lineHeight: 1.25,
                      noWrap: true
                    }}
                  />
                </StyledListItem>
              </ListItem>
            ))}
          </List>
          <Box width="100%" flexShrink={0} pt={1}>
            <ListItem disablePadding sx={{ display: "block" }}>
              <Box
                sx={{
                  p: "1px",
                  borderRadius: `${COLORS.radius.md}px`,
                  background: `linear-gradient(90deg, ${COLORS.gradients.button.left}, ${COLORS.gradients.button.right})`
                }}
              >
                <StyledListItem
                  to="/login"
                  onClick={onLogout}
                  sx={{
                    borderRadius: `${COLORS.radius.md - 1}px`,
                    backgroundColor: COLORS.gradients.sidebar.bottom
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 34, color: "inherit" }}>
                    <Logout fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={t["Logout"]}
                    primaryTypographyProps={{
                      fontSize: "0.875rem",
                      fontWeight: 500,
                      lineHeight: 1.25,
                      noWrap: true
                    }}
                  />
                </StyledListItem>
              </Box>
            </ListItem>
          </Box>
        </Box>
      </Drawer>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          marginLeft: { md: `${drawerWidth}px` },
          minHeight: { xs: "calc(100dvh - 56px)", md: "100dvh" },
          boxSizing: "border-box",
          p: { xs: 1, md: 1.5 },
          display: "flex",
          bgcolor: "transparent"
        }}
      >
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            bgcolor: COLORS.surface.page,
            borderRadius: { xs: `${COLORS.radius.lg}px`, md: `${COLORS.radius.xl}px` },
            boxShadow: COLORS.shadow.panel,
            overflow: "auto",
            p: { xs: 2, md: "22px" },
            color: COLORS.surface.primaryText
          }}
        >
          <ThemeProvider theme={contentTheme}>
            <BreakGlassModal />
            {children}
          </ThemeProvider>
        </Box>
      </Box>
    </Fragment>
  );
}
